import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { useAuth } from "common/hooks";

import {
  IMPLEMENTED_STEPS,
  firstUnansweredStep,
  sequenceForAnswers,
} from "../constants";
import type {
  OnboardingAnswers,
  OnboardingBusinessClaim,
  OnboardingCity,
  OnboardingStepName,
} from "../types";
import {
  onboardingV2AnswersAtom,
  onboardingV2BusinessClaimDraftAtom,
  onboardingV2CompletedAtom,
  onboardingV2EngagedAtom,
  onboardingV2MinimisedAtom,
  onboardingV2MinimisedPreferenceAtom,
  onboardingV2ModalRequestedAtom,
  onboardingV2PendingNewUserAtom,
  onboardingV2PresentationAtom,
  onboardingV2ReplayAtom,
  onboardingV2StepAtom,
} from "./atoms";
import { useResetOnboarding } from "./useResetOnboarding";
import {
  trackQuestionsCompleted,
  trackQuestionsMinimised,
} from "@/analytics/onboardingAnalytics";

/**
 * Drives the onboarding flow: reads and writes the answers, moves between steps
 * and closes the modal when the user is done.
 *
 * Every step calls this rather than touching the atoms directly — the same
 * contract native's `useOnboardingFlow` sets, minus the navigator (desktop is
 * one surface swapping its body, not a stack of screens).
 *
 * Answers and the step are both persisted, so the docked sheet picks up where
 * the visitor left it on any page, in any tab.
 */
export function useOnboardingFlow() {
  const [answers, setAnswers] = useAtom(onboardingV2AnswersAtom);
  const [storedStep, setStep] = useAtom(onboardingV2StepAtom);
  const setCompleted = useSetAtom(onboardingV2CompletedAtom);
  const setReplay = useSetAtom(onboardingV2ReplayAtom);
  const setPendingNewUser = useSetAtom(onboardingV2PendingNewUserAtom);
  const setModalRequested = useSetAtom(onboardingV2ModalRequestedAtom);
  const setBusinessClaimDraft = useSetAtom(onboardingV2BusinessClaimDraftAtom);
  const setMinimisedPreference = useSetAtom(
    onboardingV2MinimisedPreferenceAtom,
  );
  const [isEngaged, setEngaged] = useAtom(onboardingV2EngagedAtom);
  const presentation = useAtomValue(onboardingV2PresentationAtom);
  const isMinimised = useAtomValue(onboardingV2MinimisedAtom);
  const isOpen = presentation !== "hidden";

  const { isLoggedIn } = useAuth();

  /**
   * Step 1 forks on native, so which steps come next depends on what has been
   * picked so far rather than on a fixed list - and "How did you hear about
   * us?" is only asked while signed out. @see `sequenceForAnswers`.
   */
  const sequence = useMemo(
    () => sequenceForAnswers(answers, { isSignedIn: !!isLoggedIn }),
    [answers, isLoggedIn],
  );
  const { steps, countedSteps, totalCountedSteps } = sequence;

  /*
   * The stored step comes out of localStorage, so it can name a step that is
   * not on this path — a value written by an older build, a branch the answers
   * have since moved off, or "How did you hear about us?" for someone who has
   * just signed in. Falling back to the first unanswered step keeps that from
   * rendering nothing, and from sending them back to step 1.
   */
  const isStoredStepOnPath = steps.includes(storedStep);
  const firstUnanswered = firstUnansweredStep(steps, answers);
  const step = isStoredStepOnPath
    ? storedStep
    : (firstUnanswered ?? steps[0]);
  const stepIndex = steps.indexOf(step);

  /**
   * Every question on this path is answered and the stored step is not on it:
   * someone on "Let’s jump in!" signed in from the navbar instead, and the
   * signed-in path has no such step. Nothing is left to ask, so the flow
   * should close rather than fall back to step 1 - @see `OnboardingFlow`,
   * which acts on this.
   */
  const isFinished = !isStoredStepOnPath && firstUnanswered === undefined;

  /**
   * 1-based counter for the "Step N of M" header; null on steps that don't show
   * one. Every step on the individual path shows one, but the intro and the
   * business branch's confirmation will not.
   */
  const countedIndex = countedSteps.indexOf(step);
  const stepNumber = countedIndex >= 0 ? countedIndex + 1 : null;

  /**
   * Counted steps behind the one on screen - what the docked pill's "N of M
   * done" reports. Counted by position rather than read off `stepNumber`,
   * which is null on an uncounted step: "Let’s jump in!" after the city is
   * every step done, not none.
   */
  const finishedSteps = countedSteps.filter(
    (counted) => steps.indexOf(counted) < stepIndex,
  ).length;

  const updateAnswers = useCallback(
    (patch: Partial<OnboardingAnswers>) => {
      setAnswers((current) => ({ ...current, ...patch }));
    },
    [setAnswers],
  );

  const goToStep = useCallback(
    (next: OnboardingStepName) => {
      if (!IMPLEMENTED_STEPS.includes(next)) {
        // A no-op rather than a crash, but loud in development — a silently
        // dead Continue is hard to spot while a branch is half-built.
        console.warn(
          `[onboarding] step "${next}" has no screen yet; add it to IMPLEMENTED_STEPS once built.`,
        );
        return;
      }
      setStep(next);
    },
    [setStep],
  );

  const goNext = useCallback(() => {
    // -1 means the current step isn't on this path at all — possible if an
    // answer changed the branch under the user. Moving them to `steps[0]` would
    // throw them back to the start, so sit still instead.
    if (stepIndex < 0) return;

    const next = steps[stepIndex + 1];
    if (!next) return;
    setEngaged(true);
    goToStep(next);
  }, [goToStep, setEngaged, stepIndex, steps]);

  /** The docked sheet's "–": collapse to the progress pill. */
  const minimise = useCallback(() => {
    trackQuestionsMinimised(stepNumber ?? 0);
    setEngaged(true);
    setMinimisedPreference(true);
  }, [setEngaged, setMinimisedPreference, stepNumber]);

  /** The progress pill: open the docked sheet back up. */
  const expand = useCallback(() => {
    setEngaged(true);
    setMinimisedPreference(false);
  }, [setEngaged, setMinimisedPreference]);

  /** Closes the flow and puts the step back where a replay would expect it. */
  const close = useCallback(() => {
    setCompleted(true);
    setReplay(false);
    setModalRequested(false);
    // Settles the debt a sign-up may have opened. Finishing the flow - or
    // deciding not to - is the only thing that discharges it; being shown it
    // once is not, which is why the flag is not cleared when it opens.
    setPendingNewUser(false);
    // So a later run (a new account on this browser) starts expanded again.
    setMinimisedPreference(null);
    /*
     * The step is deliberately left where it is. The sheet is still animating
     * out when this runs, and resetting it here flipped the leaving card's
     * "Step 4 of 4" to "Step 1 of 4" mid-exit. Every way back into the flow
     * resets it on the way in instead: a replay, a full reset, and a log out
     * (which is also the only route to a second account's run).
     */
  }, [
    setCompleted,
    setMinimisedPreference,
    setModalRequested,
    setPendingNewUser,
    setReplay,
  ]);

  /**
   * The modal header's ✕. Native has no dismiss — the desktop frame draws one, and
   * the only sane meaning for it on the web is the one the carousel's "Skip"
   * had: asked and answered, don't ask again. The answers collected so far are
   * kept rather than cleared, so a replay resumes from them.
   */
  const dismiss = close;

  /**
   * Picking a city is the last question, as on native.
   *
   * Recording the city is all this does; pointing the home page at it is the
   * caller's job (`CityStep` writes `manualLocationAtom`).
   *
   * The flow ends here unless the path has a screen after the city - which
   * only the signed-out one does, "Let’s jump in!" - and then that screen
   * ends it instead.
   */
  const completeWithCity = useCallback(
    (city: OnboardingCity) => {
      trackQuestionsCompleted(step, stepNumber ?? 0, answers, city);
      setAnswers((current) => ({ ...current, city }));
      const next = stepIndex >= 0 ? steps[stepIndex + 1] : undefined;
      if (next) {
        setEngaged(true);
        goToStep(next);
        return;
      }
      close();
    },
    [
      answers,
      close,
      goToStep,
      setAnswers,
      setEngaged,
      step,
      stepIndex,
      stepNumber,
      steps,
    ],
  );

  /**
   * Records a claim the backend has accepted and moves on to its
   * confirmation. Only called after the POST succeeds, so the confirmation
   * never thanks anyone for a claim that was not filed.
   */
  const submitBusinessClaim = useCallback(
    (businessClaim: OnboardingBusinessClaim) => {
      setAnswers((current) => ({ ...current, businessClaim }));
      setBusinessClaimDraft(null);
      goNext();
    },
    [goNext, setAnswers, setBusinessClaimDraft],
  );

  /**
   * Wipes answers and position so the flow can be walked again.
   * @see useResetOnboarding, which owns the list of what gets cleared - the
   * developer-only menu row calls it directly rather than mounting this hook.
   */
  const resetOnboarding = useResetOnboarding();

  /** Re-opens the flow with the existing answers intact. */
  const replayOnboarding = useCallback(() => {
    setStep("USER_TYPE");
    setReplay(true);
  }, [setReplay, setStep]);

  return useMemo(
    () => ({
      isOpen,
      presentation,
      stepIndex,
      isMinimised,
      isEngaged,
      minimise,
      expand,
      step,
      stepNumber,
      finishedSteps,
      totalSteps: totalCountedSteps,
      isFinished,
      close,
      answers,
      updateAnswers,
      goToStep,
      goNext,
      completeWithCity,
      submitBusinessClaim,
      dismiss,
      resetOnboarding,
      replayOnboarding,
    }),
    [
      answers,
      close,
      completeWithCity,
      dismiss,
      goNext,
      goToStep,
      expand,
      finishedSteps,
      isEngaged,
      isFinished,
      isMinimised,
      isOpen,
      minimise,
      presentation,
      replayOnboarding,
      resetOnboarding,
      step,
      submitBusinessClaim,
      stepIndex,
      stepNumber,
      totalCountedSteps,
      updateAnswers,
    ],
  );
}
